/**
 * Settings — DESIGN.md §13.1, §12.2 (`PATCH /me`, `PUT /me/password`).
 *
 * Profile, password, and — from M4 — the notification preferences §11.2
 * describes: email mode, the instant exceptions for mentions and assignments,
 * and the per-kind mute list the fan-out honours.
 */
import { useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  EMAIL_MODES,
  NOTIFICATION_KINDS,
  passwordSchema,
  type UpdateMeRequest,
  type UpdateNotificationPreferencesRequest,
} from '@slate/shared';
import type { NotificationPreferences } from '@slate/shared';
import { ApiError, http } from '@/lib/api';
import { meQueryKey, useCurrentUser } from '@/lib/auth';
import { Page } from '@/components/page';
import { AvatarSection } from '@/components/settings/avatar-section';
import { useTheme, type ThemeMode } from '@/lib/theme';
import {
  TEXT_SIZES,
  TEXT_SIZE_LABELS,
  TEXT_SIZE_PX,
  useTextSize,
  type TextSize,
} from '@/lib/prefs';
import { TokenSection } from '@/components/tokens/token-section';
import { ReminderSection } from '@/components/settings/reminder-section';
import { useEmailTest, useSendEmailTest } from '@/features/email-test/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

const EMAIL_MODE_LABELS: Record<(typeof EMAIL_MODES)[number], string> = {
  instant: 'Instant',
  hourly_digest: 'Hourly digest',
  daily_digest: 'Daily digest',
  off: 'In-app only',
};

function SettingsPage(): JSX.Element {
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.name);
  const [timezone, setTimezone] = useState(me.timezone);

  const updateProfile = useMutation({
    mutationFn: (input: UpdateMeRequest) => http.patch('/me', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      toast.success('Profile updated');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'Update failed'),
  });

  return (
    <Page
      title="Settings"
      description="Appearance, your profile, password, notification preferences, and agent access."
    >
      <div className="space-y-6">
        <AppearanceSection />

        {/* #53 — your face: a photo, or your initials on a colour. */}
        <AvatarSection me={me} />

        <section className="bg-card rounded-lg border p-5">
          <h2 className="text-sm font-semibold">Profile</h2>
          <form
            className="mt-4 grid gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              updateProfile.mutate({ name, timezone });
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="timezone">Timezone</Label>
              <Input
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                aria-describedby="timezone-hint"
                required
              />
              <p id="timezone-hint" className="text-muted-foreground text-xs">
                Due dates and digests are interpreted in this timezone.
              </p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Email</Label>
              <p className="text-muted-foreground text-sm">
                {me.email}
                {me.hasGoogle ? (
                  <Badge variant="secondary" className="ml-2">
                    Google linked
                  </Badge>
                ) : null}
              </p>
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={updateProfile.isPending || (name === me.name && timezone === me.timezone)}
              >
                {updateProfile.isPending ? <Spinner /> : null}
                Save profile
              </Button>
            </div>
          </form>
        </section>

        <PasswordSection hasPassword={me.hasPassword} />

        <NotificationSection />

        <ReminderSection />

        <EmailTestSection />
        {/* §12.5 — Settings › API & Agents. */}
        <TokenSection />
      </div>
    </Page>
  );
}

/**
 * Appearance — DESIGN.md §13; Long-Day Review (Brad: "we will be using this
 * ZOOMED IN").
 *
 * Text size is a *device* preference, not an account one, so it lives in
 * localStorage and changes the root font-size — everything in the app is sized
 * in rem, so one attribute scales the whole UI without the reflow surprises of
 * browser zoom. The preview line is the point of the control: the effect is
 * already visible on this page as you press, but the sample says what "Larger"
 * means before you commit to it.
 */
function AppearanceSection(): JSX.Element {
  const { mode, setMode } = useTheme();
  const [textSize, setTextSize] = useTextSize();

  return (
    <section className="bg-card rounded-lg border p-5">
      <h2 className="text-sm font-semibold">Appearance</h2>
      <p className="text-muted-foreground mt-1 text-sm">
        Saved on this device, so a laptop and a large monitor can differ.
      </p>

      <div className="mt-4 space-y-1.5">
        <span className="text-xs font-medium" id="theme-label">
          Theme
        </span>
        <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="theme-label">
          {THEME_OPTIONS.map((option) => (
            <SegmentedButton
              key={option.mode}
              selected={mode === option.mode}
              onClick={() => setMode(option.mode)}
            >
              {option.label}
            </SegmentedButton>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-1.5">
        <span className="text-xs font-medium" id="text-size-label">
          Text size
        </span>
        <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="text-size-label">
          {TEXT_SIZES.map((size) => (
            <SegmentedButton
              key={size}
              selected={textSize === size}
              onClick={() => setTextSize(size)}
            >
              {TEXT_SIZE_LABELS[size]}
              <span className="text-muted-foreground ml-1.5 font-normal">
                {TEXT_SIZE_PX[size]}px
              </span>
            </SegmentedButton>
          ))}
        </div>
        <p className="bg-muted/40 mt-2 rounded-md border p-3 text-sm" aria-live="polite">
          Preview — a card title and its meta line look like this. {CURRENT_HINT[textSize]}
        </p>
      </div>
    </section>
  );
}

const THEME_OPTIONS: { mode: ThemeMode; label: string }[] = [
  { mode: 'dark', label: 'Dark' },
  { mode: 'light', label: 'Light' },
  { mode: 'system', label: 'Match system' },
];

const CURRENT_HINT: Record<TextSize, string> = {
  sm: 'Compact fits the most on screen.',
  md: 'Default matches the browser standard.',
  lg: 'Large is easier for a long day.',
  xl: 'Larger is the roomiest that still fits a full day on screen.',
  '2xl': 'Larger still is about the same as 150% browser zoom.',
  '3xl': 'Huge trades how much fits for how easily it reads.',
  '4xl': 'Maximum is the largest there is — lists will scroll more.',
};

/** A wrapping segmented control: `aria-pressed` rather than a native radio,
 *  because these apply immediately and there is nothing to submit. */
function SegmentedButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
        selected
          ? 'border-primary bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-accent/40',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Notification preferences — DESIGN.md §11.2.
 *
 * The copy carries the semantics, because the semantics are the feature: what
 * "hourly digest" actually means, that a quiet hour sends nothing, and that
 * mentions and assignments jump the queue.
 */
function NotificationSection(): JSX.Element {
  const queryClient = useQueryClient();
  const { data: preferences } = useQuery({
    queryKey: ['me', 'notification-preferences'] as const,
    queryFn: () => http.get<NotificationPreferences>('/me/notification-preferences'),
  });

  const save = useMutation({
    mutationFn: (input: UpdateNotificationPreferencesRequest) =>
      http.put<NotificationPreferences>('/me/notification-preferences', input),
    onSuccess: async (next) => {
      queryClient.setQueryData(['me', 'notification-preferences'], next);
      // The mode shows on `/me` too, so the shell stays consistent.
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      toast.success('Notification preferences saved');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save preferences'),
  });

  if (!preferences) {
    return (
      <section className="bg-card rounded-lg border p-5">
        <h2 className="text-sm font-semibold">Notifications</h2>
        <Spinner />
      </section>
    );
  }

  const muted = new Set(preferences.mutedKinds);

  return (
    <section className="bg-card rounded-lg border p-5">
      <h2 className="text-sm font-semibold">Notifications</h2>
      <p className="text-muted-foreground mt-1 text-sm">
        The Inbox always shows everything. This is only about email.
      </p>

      <div className="mt-4 space-y-2">
        {EMAIL_MODES.map((mode) => (
          <label
            key={mode}
            className={cn(
              'flex cursor-pointer items-start gap-2.5 rounded-md border p-3 transition-colors',
              preferences.emailMode === mode ? 'border-primary bg-accent/40' : 'hover:bg-accent/20',
            )}
          >
            <input
              type="radio"
              name="email-mode"
              className="mt-1"
              checked={preferences.emailMode === mode}
              onChange={() => save.mutate({ emailMode: mode })}
            />
            <span className="min-w-0">
              <span className="flex items-center gap-2 text-sm font-medium">
                {EMAIL_MODE_LABELS[mode]}
                {mode === 'hourly_digest' ? <Badge variant="secondary">Default</Badge> : null}
              </span>
              <span className="text-muted-foreground block text-xs">{EMAIL_MODE_HELP[mode]}</span>
            </span>
          </label>
        ))}
      </div>

      {/* §11.2's exceptions — "you're personally needed" beats batching. */}
      <fieldset className="mt-5">
        <legend className="text-xs font-medium">Always email immediately</legend>
        <p className="text-muted-foreground mb-2 text-xs">
          Even on a digest. Turn these off and they wait for the digest like everything else.
        </p>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={preferences.instantForMentions}
              onChange={(event) => save.mutate({ instantForMentions: event.target.checked })}
            />
            When someone @mentions me
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={preferences.instantForAssignments}
              onChange={(event) => save.mutate({ instantForAssignments: event.target.checked })}
            />
            When a card is assigned to me
          </label>
        </div>
      </fieldset>

      {/* §11.1 — `muted_kinds` filters the fan-out itself, in-app included. */}
      <fieldset className="mt-5">
        <legend className="text-xs font-medium">Mute entirely</legend>
        <p className="text-muted-foreground mb-2 text-xs">
          A muted kind produces no notification at all — no email, and nothing in your Inbox.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {NOTIFICATION_KINDS.map((kind) => {
            const on = muted.has(kind);
            return (
              <button
                key={kind}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  save.mutate({
                    mutedKinds: on
                      ? preferences.mutedKinds.filter((value: string) => value !== kind)
                      : [...preferences.mutedKinds, kind],
                  })
                }
                className={cn(
                  'rounded-full border px-2 py-0.5 text-xs transition-colors',
                  on
                    ? 'border-destructive/40 bg-destructive/10 text-destructive'
                    : 'text-muted-foreground hover:bg-accent',
                )}
                title={
                  on ? `Muted — click to unmute ${KIND_LABELS[kind]}` : `Mute ${KIND_LABELS[kind]}`
                }
              >
                {on ? '🔇 ' : ''}
                {KIND_LABELS[kind]}
              </button>
            );
          })}
        </div>
      </fieldset>
    </section>
  );
}

const EMAIL_MODE_HELP: Record<(typeof EMAIL_MODES)[number], string> = {
  instant:
    'One email per change, within about a minute. Rapid edits to the same card arrive as one email.',
  hourly_digest:
    'One email an hour, grouped by card — and nothing at all in an hour where nothing happened.',
  daily_digest: 'One email a morning at 08:00 your time, grouped by card.',
  off: 'No email. Everything still appears in your Inbox.',
};

const KIND_LABELS: Record<(typeof NOTIFICATION_KINDS)[number], string> = {
  mention: 'Mentions',
  assignment: 'Assignments',
  comment: 'Comments',
  status_change: 'Status changes',
  field_change: 'Field changes',
  attachment: 'Attachments',
  dependency: 'Dependencies',
  completion: 'Completions',
  project_status: 'Project updates',
  // #99 — renamed from "Due-date reminders" once real reminders existed: this
  // is the 07:00 list of what is due, not a reminder anyone set.
  due_soon: 'Morning due-date list',
  poll: 'Polls',
  reminder: 'Reminders',
};

function PasswordSection({ hasPassword }: { hasPassword: boolean }): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const changePassword = useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      http.put<void>('/me/password', input),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      // §9.1 — changing a password signs out every other device.
      toast.success('Password changed. Other devices have been signed out.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not change password'),
  });

  const invalid = newPassword.length > 0 && !passwordSchema.safeParse(newPassword).success;

  return (
    <section className="bg-card rounded-lg border p-5">
      <h2 className="text-sm font-semibold">Password</h2>
      {hasPassword ? (
        <form
          className="mt-4 grid max-w-md gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (invalid) return;
            changePassword.mutate({ currentPassword, newPassword });
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="current">Current password</Label>
            <Input
              id="current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">New password</Label>
            <Input
              id="new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              aria-invalid={invalid}
              required
            />
            {invalid ? (
              <p className="text-destructive text-sm">Use at least 12 characters.</p>
            ) : null}
          </div>
          <Button
            type="submit"
            className="justify-self-start"
            disabled={changePassword.isPending || invalid || newPassword.length === 0}
          >
            {changePassword.isPending ? <Spinner /> : null}
            Change password
          </Button>
        </form>
      ) : (
        <p className="text-muted-foreground mt-2 text-sm">
          This account signs in with Google and has no password. Ask an administrator if you need
          one.
        </p>
      )}
    </section>
  );
}

/**
 * "Is email actually working?" — answered in one click, in both directions.
 * Outbound is live: the worker sends and reports the relay's verdict. Inbound
 * (reply-by-email) shows its real state, which today is "not set up".
 */
function EmailTestSection(): JSX.Element {
  const { data } = useEmailTest();
  const send = useSendEmailTest();
  const test = data?.test ?? null;
  const busy = send.isPending || test?.status === 'queued';

  return (
    <section className="bg-card rounded-lg border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Email test</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Send yourself a message to confirm Slate's mail reaches your inbox.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            send.mutate(undefined, {
              onError: (error) =>
                toast.error(error instanceof ApiError ? error.message : 'Could not start the test'),
            });
          }}
          disabled={busy}
        >
          {busy ? (
            <>
              <Spinner className="mr-2" /> Sending…
            </>
          ) : (
            'Send me a test email'
          )}
        </Button>
      </div>

      <dl className="mt-4 divide-y text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 py-2.5">
          <dt className="font-medium">Outbound (Slate → you)</dt>
          <dd className="flex items-center gap-2">
            {!test && <span className="text-muted-foreground">Not tested yet</span>}
            {test?.status === 'queued' && <Badge variant="secondary">Sending…</Badge>}
            {test?.status === 'sent' && (
              <>
                <Badge variant="success">Delivered to relay</Badge>
                <span className="text-muted-foreground">
                  to {test.to} at {formatTime(test.finishedAt ?? test.requestedAt)}
                </span>
              </>
            )}
            {test?.status === 'failed' && <Badge variant="destructive">Failed</Badge>}
          </dd>
        </div>
        {test?.status === 'failed' && test.error && (
          <div className="py-2.5">
            <p className="text-destructive text-sm">{test.error}</p>
            <p className="text-muted-foreground mt-1 text-xs">
              This is the mail server's own message. It usually means the relay does not accept
              Slate's address yet, or the address above is wrong.
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 py-2.5">
          <dt className="font-medium">Inbound (reply → comment)</dt>
          <dd className="flex items-center gap-2">
            {test?.inbound.status === 'received' && <Badge variant="success">Reply received</Badge>}
            {test?.inbound.status === 'waiting' && (
              <span className="text-muted-foreground">
                Waiting for your reply to the test email…
              </span>
            )}
            {(!test || test.inbound.status === 'not-configured') && (
              <>
                <Badge variant="secondary">Not set up</Badge>
                <span className="text-muted-foreground">
                  Replying to Slate emails is not enabled yet.
                </span>
              </>
            )}
          </dd>
        </div>
      </dl>
      {test?.status === 'sent' && (
        <p className="text-muted-foreground mt-3 text-xs">
          "Delivered to relay" means Google accepted the message. If it is not in your inbox within
          a minute, check spam, then the address on your profile.
        </p>
      )}
    </section>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
